<template>
  <property-report-card
    title="Rents by Unit Type"
    :description="
      printingMode
        ? 'Track rent and availability trends by unit type.'
        : 'Track rent and availability trends by unit type. Click to explore underlying listings.'
    "
    :printing-mode="printingMode"
    :is-empty="!unitsFound(filteredProperties)"
  >
    <q-dialog v-model="displayUnitPopup" transition-duration="100" @before-hide="closeUnitPopup">
      <q-card v-if="clickedCell" style="max-width: 1600px">
        <q-card-section>
          <div class="row justify-between">
            <div>
              <span
                :class="
                  groupBySelection === 'Unit Type'
                    ? `hd-${parseInt(clickedCell.title.split(' ')[0])}-bed-bg hd-badge`
                    : ''
                "
              >
                {{ clickedCell.title }}
              </span>
              <div class="row q-gutter-x-sm items-center">
                <div class="text-h5 text-bold">
                  {{ clickedCell.fieldLabel }} -
                  {{
                    clickedCell.field.formattedValue === undefined
                      ? clickedCell.field.value
                      : clickedCell.field.formattedValue
                  }}
                  {{ clickedCell.field.unit ? clickedCell.field.unit : '' }}
                </div>
              </div>
            </div>
            <q-icon size="sm" name="close" class="cursor-pointer" @click="closeUnitPopup" />
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <property-compare-unit-details
            :expanded-unit-history="expandedUnitHistory"
            :units-with-properties="currentClickedCellUnits"
            :date-range="dateRange"
            hide-filter
          />
        </q-card-section>
      </q-card>
    </q-dialog>
    <template #actions>
      <q-btn id="rents-by-unit-type_download" flat no-caps @click="exportCSV">
        <q-icon name="download" class="q-mr-sm" size="medium" />Download
      </q-btn>
      <q-select
        v-model="metric"
        transition-duration="100"
        :options="metricOptions"
        label="Trend"
        dense
        outlined
        style="width: 180px"
        @update:model-value="(val) => $tracker.changedFilter('metric', 'unit_types', val)"
      />
      <div class="row items-center q-gutter-sm">
        <q-select
          v-model="groupBySelection"
          transition-duration="100"
          class="overflow-select"
          style="width: 150px"
          dense
          outlined
          label="Group By"
          :options="['Property', 'Unit Type']"
          @update:model-value="(value) => $tracker.changedFilter('group_by', 'unit_types', value)"
        />
        <q-btn
          v-if="subjectDetail"
          id="rents-by-unit-type_pin-subject"
          flat
          dense
          round
          aria-label="Pin subject property"
          :aria-pressed="pinSubjectRow"
          @click="togglePinSubjectRow"
        >
          <q-icon name="push_pin" size="xs" :class="pinIconClass" />
          <q-tooltip max-width="250px">
            <b>Pin Subject Property</b>
            <br />
            Keep the subject property at the top of its section when sorting.
          </q-tooltip>
        </q-btn>
      </div>
    </template>
    <q-table
      id="intercom-rents-by-unit-type-content"
      :class="{ 'sticky-header-table': !printingMode }"
      :rows="rows"
      :columns="columns"
      :rows-per-page-options="[0]"
      :sort-method="sortRows"
      hide-bottom
      dense
      bordered
      row-key="id"
      :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
    >
      <template #header-cell="props">
        <q-th :props="props" @click="sortingChanged(props.col.name)">
          {{ props.col.label }}
          <q-tooltip v-if="props.col.description" max-width="250px">
            <b>{{ props.col.label }}</b>
            <br />
            {{ props.col.description }}
          </q-tooltip>
        </q-th>
      </template>
      <template #body="props">
        <q-tr
          :props="props"
          :class="{
            'text-bold bg-deep-purple-light text-primary subject':
              subjectDetail && props.row.id === subjectDetail.id
          }"
        >
          <q-td
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            :class="{ 'visible-on-hover-trigger': col.name === 'label' }"
          >
            <div v-if="col.name === 'Trend' && props.row.trend" class="row justify-center">
              <chart-sparkline
                :values="props.row.trend"
                :label="`${metric.value === 'effective_rent' ? 'Avg Effective Rent PSF' : 'Avg Asking Rent PSF'}`"
              />
            </div>
            <template
              v-else-if="
                col.field === Column.CONCESSION_PERCENT || col.field === Column.CONCESSION_AMOUNT
              "
            >
              <show-concessions-popup-table-cell :property="props.row.prop" :date-range="dateRange">
                {{ col.value }}
              </show-concessions-popup-table-cell>
            </template>
            <template v-else-if="col.field === Column.TMLP_FEES">
              <show-fees-popup-table-cell
                :fees="props.row?.prop.fees?.filter((_) => _.frequency === 'month') ?? []"
                tracker-location="unit_types"
              >
                {{ col.value }}
              </show-fees-popup-table-cell>
            </template>
            <span
              v-else
              :class="[
                col.name === 'label' ? 'constrained-cell' : '',
                props.row[col.field]?.units?.length ? 'clickable-table-cell' : ''
              ]"
              @click="
                col.name !== 'label' &&
                openUnitPopup(
                  props.row.label,
                  col.label,
                  props.row.prop,
                  {
                    value: props.row[col.field].value,
                    formattedValue: getColumnFormattedValue(col.field, props.row),
                    unit: col.unit
                  },
                  props.row[col.field].units
                )
              "
            >
              <span v-if="col.name === 'label'" class="row items-center label-cell">
                <q-btn
                  v-if="props.row.units?.length && !printingMode"
                  size="xs"
                  class="q-mr-xs"
                  flat
                  dense
                  :icon="props.expand ? 'expand_less' : 'expand_more'"
                  @click.stop="togglePropertyRow(props)"
                />
                <span
                  v-if="groupBySelection === 'Unit Type'"
                  :class="`hd-${parseInt(props.row.label.split(' ')[0])}-bed-bg hd-badge`"
                >
                  {{ props.row.label }}
                </span>
                <span v-else>
                  {{ col.value }}
                </span>
                <q-btn
                  size="xs"
                  dense
                  flat
                  no-caps
                  class="q-ml-sm visible-on-hover print-hide"
                  color="primary"
                  @click.stop="expandAllRows(props)"
                >
                  <q-icon name="expand"></q-icon>
                  <q-tooltip> Expand All </q-tooltip>
                </q-btn>
              </span>
              <span v-else>
                {{ col.value }}
              </span>
            </span>
          </q-td>
        </q-tr>
        <template v-if="props.expand">
          <template v-for="row in getChildrenRows(props.row)" :key="row.id">
            <q-tr
              :props="props"
              :class="[
                $q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1',
                { 'text-bold bg-deep-purple-light text-primary subject': row.isSubject }
              ]"
            >
              <q-td v-for="col in props.cols" :key="col.field" class="text-center">
                <div
                  v-if="col.name === 'label'"
                  class="q-pl-md row justify-start items-center label-cell"
                >
                  <q-btn
                    size="xs"
                    class="q-mr-sm"
                    flat
                    dense
                    :icon="
                      expandedBedRows.get(row.id) === props.row.id ? 'expand_less' : 'expand_more'
                    "
                    @click.stop="toggleBed(row, props.row)"
                  />
                  <div
                    v-if="groupBySelection === 'Property'"
                    :class="`hd-${parseInt(row.label.split(' ')[0])}-bed-bg hd-badge`"
                  >
                    {{ row.label }}
                  </div>
                  <div v-else>
                    {{ row.label }}
                  </div>
                </div>
                <div v-else-if="col.name === 'Trend'" class="row justify-center">
                  <chart-sparkline
                    :values="row.trend"
                    :label="`${metric.value === 'effective_rent' ? 'Avg Effective Rent PSF' : 'Avg Asking Rent PSF'}`"
                  />
                </div>
                <template v-else-if="col.field === Column.CONCESSION_PERCENT">
                  <show-concessions-popup-table-cell
                    v-if="row.units[0]?.prop"
                    :property="row.units[0]?.prop"
                    :date-range="dateRange"
                  >
                    {{ getColumnFormattedValue(col.field, row) }}
                  </show-concessions-popup-table-cell>
                </template>
                <template v-else-if="col.field === Column.TMLP_FEES">
                  <show-fees-popup-table-cell
                    :fees="row?.units[0]?.prop.fees?.filter((_) => _.frequency === 'month') ?? []"
                    tracker-location="unit_types"
                  >
                    {{ getColumnFormattedValue(col.field, row) }}
                  </show-fees-popup-table-cell>
                </template>
                <span
                  v-else
                  :class="row[col.field]?.units?.length ? 'clickable-table-cell' : ''"
                  @click="
                    openUnitPopup(
                      props.row.label,
                      col.label,
                      row.prop,
                      {
                        value: row[col.field].value,
                        formattedValue: getColumnFormattedValue(col.field, row),
                        unit: col.unit
                      },
                      row[col.field].units
                    )
                  "
                >
                  {{ getColumnFormattedValue(col.field, row) }}
                </span>
              </q-td>
            </q-tr>
            <template v-if="expandedBedRows.get(row.id) === props.row.id">
              <q-tr
                v-for="floorplan in getFloorplanRows(row)"
                :key="floorplan.name"
                :class="[$q.dark.isActive ? 'bg-grey-8' : 'bg-grey-3']"
              >
                <q-td v-for="col in props.cols" :key="col.field" class="text-center">
                  <div
                    v-if="col.name === 'label'"
                    class="q-pl-xxl row justify-start relative-position"
                  >
                    <div class="floorplan-line" :class="`hd-${getBedCount(row)}-bed-bg`"></div>
                    <div class="text-caption q-pl-xl">
                      {{ floorplan.name }}
                    </div>
                  </div>
                  <div v-else-if="col.name === 'Trend'" class="row justify-center">
                    <chart-sparkline
                      :values="floorplan.trend"
                      :label="`${metric.value === 'effective_rent' ? 'Avg Effective Rent PSF' : 'Avg Asking Rent PSF'}`"
                    />
                  </div>
                  <template v-else-if="col.field === Column.CONCESSION_PERCENT">
                    <show-concessions-popup-table-cell
                      :property="floorplan.units[0]?.prop"
                      :date-range="dateRange"
                    >
                      {{ getColumnFormattedValue(col.field, floorplan) }}
                    </show-concessions-popup-table-cell>
                  </template>
                  <template v-else-if="col.field === Column.TMLP_FEES">
                    <show-fees-popup-table-cell
                      :fees="
                        floorplan?.units[0]?.prop.fees?.filter((_) => _.frequency === 'month') ?? []
                      "
                      tracker-location="unit_types"
                    >
                      {{ getColumnFormattedValue(col.field, floorplan) }}
                    </show-fees-popup-table-cell>
                  </template>
                  <span
                    v-else
                    :class="floorplan[col.field]?.units?.length ? 'clickable-table-cell' : ''"
                    @click="
                      openUnitPopup(
                        props.row.label,
                        col.label,
                        floorplan.prop,
                        {
                          value: floorplan[col.field].value,
                          formattedValue: getColumnFormattedValue(col.field, floorplan),
                          unit: col.unit
                        },
                        floorplan[col.field].units
                      )
                    "
                  >
                    {{ getColumnFormattedValue(col.field, floorplan) }}
                  </span>
                </q-td>
              </q-tr>
            </template>
          </template>
        </template>
        <q-tr
          v-if="props.rowIndex === rows.length - 1"
          class="text-bold text-center title-row"
          :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
        >
          <q-td class="constrained-cell text-left">
            <div>
              Comp Average
              <q-icon v-if="subjectDetail && !printingMode" name="info_outline">
                <q-tooltip>
                  Computed with underlying units (Does not include
                  {{ getPropertyDisplayName(subjectDetail) }})
                </q-tooltip>
              </q-icon>
            </div>
          </q-td>
          <q-td v-for="(val, i) in footerRow" :key="i" class="text-center">
            {{ val }}
          </q-td>
        </q-tr>
      </template>
    </q-table>
  </property-report-card>
</template>

<script lang="ts">
import { type PropType } from 'vue'
import { format, min, parseISO } from 'date-fns'
import { exportFile } from 'quasar'
import {
  type Availability,
  type AvailabilityPeriod,
  avgPsf,
  Column,
  daysOnMarket,
  getTMLPFees,
  getTrend,
  type Price,
  type PropertyDetailsResponse
} from 'common'
import type { ExpandedUnitHistory } from '@/types/propertyReports'
import {
  avg,
  filterColumns,
  formatMoney,
  formatNumber,
  formatPercent,
  unitTypeColor,
  type ColumnData,
  sortColumns,
  getTrendDiffPerc,
  unitsFound
} from '@/utils'
import ChartSparkline from '@/components/Charts/ChartSparkline.vue'
import ShowConcessionsPopupTableCell from '@/components/PropertyReports/ShowConcessionsPopupTableCell.vue'
import PropertyCompareUnitDetails from '@/components/PropertyReports/PropertyCompareUnitDetails.vue'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import ShowFeesPopupTableCell from '@/components/PropertyReports/ShowFeesPopupTableCell.vue'

interface UnitData {
  unit: Availability
  lastHistory: Price
  period: AvailabilityPeriod
  prop: PropertyDetailsResponse
}

interface ColumnCellData {
  units: UnitData[]
  value: string | number | null
}

type MetricOption = 'asking_rent' | 'effective_rent'

const safeMin = (vals: (number | null)[]) => {
  const cleanVals = vals.filter((_) => _) as number[]
  return cleanVals.length ? Math.min(...cleanVals) : null
}
const safeMax = (vals: (number | null)[]) => {
  const cleanVals = vals.filter((_) => _) as number[]
  return cleanVals.length ? Math.max(...cleanVals) : null
}

export default {
  name: 'PropertyCompareUnitTypeTable',
  components: {
    ShowFeesPopupTableCell,
    PropertyReportCard,
    PropertyCompareUnitDetails,
    ShowConcessionsPopupTableCell,
    ChartSparkline
  },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: UnitData[]
      }>,
      required: true
    },
    expandedUnitHistory: {
      type: Object as PropType<ExpandedUnitHistory>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    visibleColumns: {
      type: Array as PropType<Column[]>,
      required: true
    },
    toggleableColumns: {
      type: Object as PropType<ColumnData>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      unitTypeColor,
      formatMoney,
      formatNumber,
      Column,
      formatPercent,
      expandedBedRows: new Map(),
      displayUnitPopup: false,
      clickedCell: null as null | {
        title: string
        fieldLabel: string
        field: {
          value: string | number | null
          formattedValue?: string
          unit?: string
        }
        units: UnitData[]
      },
      metric: { label: 'Asking Rent', value: 'asking_rent' } as {
        label: string
        value: MetricOption
      },
      metricOptions: [
        { label: 'Asking Rent', value: 'asking_rent' },
        { label: 'Effective Rent', value: 'effective_rent' }
      ] as { label: string; value: MetricOption }[],
      trendDiffCache: {},
      currentSort: null as {
        column: string
        direction: 'asc' | 'desc'
      } | null,
      groupBySelection: 'Property' as 'Property' | 'Unit Type',
      pinSubjectRow: false
    }
  },
  computed: {
    pinIconClass() {
      if (this.pinSubjectRow) {
        return 'text-primary'
      }
      return this.$q.dark.isActive ? 'text-white' : 'text-black'
    },
    footerRow(): (string | null)[] {
      const initialFooterData = {
        currently_available: [] as number[],
        days_on_market: [] as (number | null)[],
        market_rent: [] as (number | null)[],
        min_market_rent: [] as (number | null)[],
        max_market_rent: [] as (number | null)[],
        effective_rent: [] as (number | null)[],
        sqft: [] as (number | null)[],
        min_sqft: [] as (number | null)[],
        max_sqft: [] as (number | null)[],
        askPsf: [] as { price: number; sqft: number }[],
        effPsf: [] as { price: number; sqft: number }[],
        total_concession_amount: [] as (number | null)[],
        concession_amount: [] as (number | null)[],
        concession_percent: [] as number[],
        year_built: [] as (number | null)[],
        tmlp: [] as (number | null)[],
        net_tmlp: [] as (number | null)[],
        tmlp_fees: [] as (number | null)[]
      }

      const footerData = this.filteredProperties.reduce((acc, { prop, units }) => {
        if (prop.id === this.subjectDetail?.id) {
          return acc
        }

        acc.year_built.push(prop.year_built)
        for (const { unit, lastHistory, period } of units) {
          const leaseTermInMonths = unit.lease_term ?? 12

          const days = daysOnMarket(period, this.dateRange.to)
          acc.days_on_market.push(days)

          const askPrice = lastHistory.price ?? lastHistory.min_price
          acc.market_rent.push(askPrice)

          const effPrice = lastHistory.effective_price || lastHistory.min_effective_price
          acc.effective_rent.push(effPrice)

          const tmlpFees = getTMLPFees(prop)
          acc.tmlp_fees.push(tmlpFees)

          if (askPrice) {
            const tmlpValue = askPrice + tmlpFees
            acc.tmlp.push(tmlpValue)
          }
          if (effPrice) {
            const netTmlpValue = effPrice + tmlpFees
            acc.net_tmlp.push(netTmlpValue)
          }
          const sqft = unit.sqft || unit.min_sqft
          acc.sqft.push(sqft)

          if (lastHistory.price && lastHistory.effective_price) {
            const monthlyConcessionAmount = lastHistory.price - lastHistory.effective_price
            acc.concession_percent.push(
              (lastHistory.price - lastHistory.effective_price) / lastHistory.price
            )
            acc.concession_amount.push(monthlyConcessionAmount)
            acc.total_concession_amount.push(monthlyConcessionAmount * leaseTermInMonths)
          }

          if (sqft && askPrice) {
            acc.askPsf.push({ price: askPrice, sqft })
          }
          if (sqft && effPrice) {
            acc.effPsf.push({ price: effPrice, sqft })
          }
        }

        const allPropPrices = units
          .flatMap(({ lastHistory }) => lastHistory.price ?? lastHistory.min_price)
          .filter((_) => _) as number[]

        if (allPropPrices.length) {
          acc.min_market_rent.push(Math.min(...allPropPrices))
          acc.max_market_rent.push(Math.max(...allPropPrices))
        }

        const allPropSfs = units
          .flatMap(({ unit }) => unit.sqft || unit.min_sqft)
          .filter((_) => _) as number[]
        if (allPropSfs.length) {
          acc.min_sqft.push(Math.min(...allPropSfs))
          acc.max_sqft.push(Math.max(...allPropSfs))
        }

        return acc
      }, initialFooterData)

      const avgValues = {
        currently_available: avg(footerData.currently_available),
        days_on_market: avg(footerData.days_on_market),
        min_sqft: avg(footerData.min_sqft),
        sqft: avg(footerData.sqft),
        max_sqft: avg(footerData.max_sqft),
        min_market_rent: avg(footerData.min_market_rent),
        market_rent: avg(footerData.market_rent),
        max_market_rent: avg(footerData.max_market_rent),
        rent_psf: avgPsf(footerData.askPsf),
        effective_rent: avg(footerData.effective_rent),
        effective_rent_psf: avgPsf(footerData.effPsf),
        concession_amount: avg(footerData.concession_amount),
        total_concession_amount: avg(footerData.total_concession_amount),
        concession_percent: avg(footerData.concession_percent),
        year_built: avg(footerData.year_built),
        tmlp: avg(footerData.tmlp),
        net_tmlp: avg(footerData.net_tmlp),
        tmlp_fees: avg(footerData.tmlp_fees)
      }

      const footerColumnValues = [
        {
          field: Column.NUMBER_LEASED,
          value: ''
        },
        {
          field: Column.CURRENTLY_AVAILABLE,
          value: formatNumber(avgValues.currently_available)
        },
        {
          field: Column.DAYS_ON_MARKET,
          value: formatNumber(avgValues.days_on_market)
        },
        {
          field: Column.MIN_SQFT,
          value: formatNumber(avgValues.min_sqft)
        },
        {
          field: Column.SQFT,
          value: formatNumber(avgValues.sqft)
        },
        {
          field: Column.MAX_SQFT,
          value: formatNumber(avgValues.max_sqft)
        },
        {
          field: Column.MIN_MARKET_RENT,
          value: formatMoney(avgValues.min_market_rent, 0)
        },
        {
          field: Column.MARKET_RENT,
          value: formatMoney(avgValues.market_rent, 0)
        },
        {
          field: Column.MAX_MARKET_RENT,
          value: formatMoney(avgValues.max_market_rent, 0)
        },
        {
          field: Column.RENT_PSF,
          value: formatMoney(avgValues.rent_psf, 2)
        },
        {
          field: Column.EFFECTIVE_RENT,
          value: formatMoney(avgValues.effective_rent, 0)
        },
        {
          field: Column.EFFECTIVE_RENT_PSF,
          value: formatMoney(avgValues.effective_rent_psf, 2)
        },
        {
          field: Column.CONCESSION_AMOUNT,
          value: formatMoney(avgValues.concession_amount, 0)
        },
        {
          field: Column.TOTAL_CONCESSION_AMOUNT,
          value: formatMoney(avgValues.total_concession_amount, 0)
        },
        {
          field: Column.CONCESSION_PERCENT,
          value: formatPercent(avgValues.concession_percent)
        },
        {
          field: Column.YEAR_BUILT,
          value: avgValues.year_built ? Math.round(avgValues.year_built).toString() : ''
        },
        {
          field: Column.TREND,
          value: ''
        },
        {
          field: Column.TMLP,
          value: formatMoney(avgValues.tmlp, 0)
        },
        {
          field: Column.NET_TMLP,
          value: formatMoney(avgValues.net_tmlp, 0)
        },
        {
          field: Column.TMLP_FEES,
          value: formatMoney(avgValues.tmlp_fees, 0)
        }
      ]

      return footerColumnValues
        .filter((column) => {
          return filterColumns(column.field, this.toggleableColumns, this.visibleColumns)
        })
        .sort((columnA, columnB) => {
          return sortColumns(columnA.field, columnB.field, this.columnOrder)
        })
        .map((_) => _.value) as (string | null)[]
    },
    columnOrder(): Column[] {
      return [Column.LABEL, ...this.visibleColumns]
    },
    currentClickedCellUnits(): UnitData[] {
      if (!this.clickedCell) {
        return []
      }

      const freshUnitsByKey = new Map<string, UnitData>()
      for (const { prop, units } of this.filteredProperties) {
        for (const unitData of units) {
          freshUnitsByKey.set(`${unitData.unit.id}:${unitData.period.period_id}`, {
            ...unitData,
            prop
          })
        }
      }

      return this.clickedCell.units.map((unitData) => {
        return freshUnitsByKey.get(`${unitData.unit.id}:${unitData.period.period_id}`) || unitData
      })
    },
    rows() {
      const value = this.filteredProperties.map(({ prop, units }) => {
        const unitsWithProp = units.map((_) => {
          return {
            ..._,
            prop: prop
          }
        })
        return {
          id: prop.id,
          prop,
          units: unitsWithProp,
          ...this.getParentRow(prop, this.getPropertyDisplayName(prop), unitsWithProp)
        }
      })
      if (this.groupBySelection === 'Unit Type') {
        const unitTypeGroups: Record<string, { prop: PropertyDetailsResponse; units: UnitData[] }> =
          {}
        for (const property of value) {
          for (const u of property.units) {
            const key = `${u.unit.bed}/${u.unit.bath}${u.unit.partial_bath ? '.5' : ''}`
            if (!(key in unitTypeGroups)) {
              unitTypeGroups[key] = { prop: property.prop, units: [] }
            }
            unitTypeGroups[key].units.push({
              ...u,
              prop: property.prop
            })
          }
        }

        const unitTypeRows = []
        for (const key in unitTypeGroups) {
          const unitTypeGroup = unitTypeGroups[key]
          const firstUnit = unitTypeGroup.units[0]?.unit
          unitTypeRows.push({
            id: key,
            prop: unitTypeGroup.prop,
            units: unitTypeGroup.units,
            bedCount: firstUnit?.bed || 0,
            bathCount: (firstUnit?.bath || 0) + (firstUnit?.partial_bath ? 0.5 : 0),
            ...this.getParentRow(unitTypeGroup.prop, key, unitTypeGroup.units)
          })
        }

        // Sort unit types in bed / bath order
        return unitTypeRows.sort((a, b) => {
          if (a.bedCount !== b.bedCount) {
            return a.bedCount - b.bedCount
          }
          return a.bathCount - b.bathCount
        })
      }
      return this.pinSubjectFirst(value, (row) => row.id === this.subjectDetail?.id)
    },
    columns() {
      return [
        { name: 'label', label: '', field: Column.LABEL, align: 'left' as const },
        {
          name: '# Leased',
          label: '# Leased',
          field: Column.NUMBER_LEASED,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.NUMBER_LEASED].description,
          unit: 'units'
        },
        {
          name: '# Active',
          label: '# Active',
          field: Column.CURRENTLY_AVAILABLE,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.CURRENTLY_AVAILABLE].description,
          unit: 'units'
        },
        {
          name: 'Days on Mkt',
          label: 'Days on Mkt',
          field: Column.DAYS_ON_MARKET,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.DAYS_ON_MARKET].description,
          unit: 'days'
        },
        {
          name: 'Min SF',
          label: 'Min SF',
          field: Column.MIN_SQFT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.MIN_SQFT].description,
          unit: 'ft²'
        },
        {
          name: 'Avg SF',
          label: 'Avg SF',
          field: Column.SQFT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.SQFT].description,
          unit: 'ft²'
        },
        {
          name: 'Max SF',
          label: 'Max SF',
          field: Column.MAX_SQFT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatNumber(x.value),
          description: this.toggleableColumns[Column.MAX_SQFT].description,
          unit: 'ft²'
        },
        {
          name: 'Min Rent',
          label: 'Min Rent',
          field: Column.MIN_MARKET_RENT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => (x.value ? formatMoney(x.value, 0) : ''),
          description: this.toggleableColumns[Column.MIN_MARKET_RENT].description
        },
        {
          name: 'Avg Rent',
          label: 'Avg Rent',
          field: Column.MARKET_RENT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) =>
            x.value ? formatMoney(Math.round(x.value)) : '',
          description: this.toggleableColumns[Column.MARKET_RENT].description
        },
        {
          name: 'Max Rent',
          label: 'Max Rent',
          field: Column.MAX_MARKET_RENT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => (x.value ? formatMoney(x.value, 0) : ''),
          description: this.toggleableColumns[Column.MAX_MARKET_RENT].description
        },
        {
          name: 'PSF',
          label: 'Avg PSF',
          field: Column.RENT_PSF,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatMoney(x.value, 2),
          description: this.toggleableColumns[Column.RENT_PSF].description,
          unit: '/ft²'
        },
        {
          name: 'NER',
          label: 'NER',
          field: Column.EFFECTIVE_RENT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) =>
            x.value ? formatMoney(Math.round(x.value)) : '',
          description: this.toggleableColumns[Column.EFFECTIVE_RENT].description
        },
        {
          name: 'EffPSF',
          label: 'NER PSF',
          field: Column.EFFECTIVE_RENT_PSF,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatMoney(x.value, 2),
          description: this.toggleableColumns[Column.EFFECTIVE_RENT_PSF].description,
          unit: '/ft²'
        },
        {
          name: 'Concession',
          label: 'Concession',
          field: Column.CONCESSION_AMOUNT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatMoney(x.value, 0),
          description: this.toggleableColumns[Column.CONCESSION_AMOUNT].description
        },
        {
          name: 'Total Concession',
          label: 'Total Concession',
          field: Column.TOTAL_CONCESSION_AMOUNT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatMoney(x.value, 0),
          description: this.toggleableColumns[Column.TOTAL_CONCESSION_AMOUNT].description
        },
        {
          name: 'Concession %',
          label: 'Concession %',
          field: Column.CONCESSION_PERCENT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => formatPercent(x.value),
          description: this.toggleableColumns[Column.CONCESSION_PERCENT].description
        },
        {
          name: 'Year Built',
          label: 'Year Built',
          field: Column.YEAR_BUILT,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) => x.value,
          description: this.toggleableColumns[Column.YEAR_BUILT].description
        },
        {
          name: 'Trend',
          field: Column.TREND,
          label: 'Trend',
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: (
            a: { value: number | null; date: string }[],
            b: { value: number | null; date: string }[],
            rowA: { id: string },
            rowB: { id: string }
          ) => {
            let aDiff, bDiff
            if (this.trendDiffCache[rowA.id] !== undefined) {
              aDiff = this.trendDiffCache[rowA.id]
            } else {
              aDiff = this.getTrendDiffPerc(a)
              this.trendDiffCache[rowA.id] = aDiff
            }
            if (this.trendDiffCache[rowB.id] !== undefined) {
              bDiff = this.trendDiffCache[rowB.id]
            } else {
              bDiff = this.getTrendDiffPerc(b)
              this.trendDiffCache[rowB.id] = bDiff
            }
            return (aDiff || 0) - (bDiff || 0)
          },
          description: this.toggleableColumns[Column.TREND].description
        },
        {
          name: 'TMLP',
          label: 'TMLP',
          field: Column.TMLP,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) =>
            x.value ? formatMoney(Math.round(x.value)) : '',
          description: this.toggleableColumns[Column.TMLP].description
        },
        {
          name: 'Net TMLP',
          label: 'Net TMLP',
          field: Column.NET_TMLP,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) =>
            x.value ? formatMoney(Math.round(x.value)) : '',
          description: this.toggleableColumns[Column.NET_TMLP].description
        },
        {
          name: 'Req. Fees',
          label: 'Req. Fees',
          field: Column.TMLP_FEES,
          align: 'center' as const,
          sortable: true,
          sortOrder: 'da' as const,
          sort: this.sortColumn,
          format: (x: { value: number | null }) =>
            x.value ? formatMoney(Math.round(x.value)) : '',
          description: this.toggleableColumns[Column.TMLP_FEES].description
        }
      ]
        .filter((column) => {
          if (column) {
            return filterColumns(column.field, this.toggleableColumns, this.visibleColumns)
          }
          return false
        })
        .sort((columnA, columnB) => {
          return sortColumns(columnA.field, columnB.field, this.columnOrder)
        })
    }
  },
  methods: {
    unitsFound,
    getTrendDiffPerc,
    closeUnitPopup() {
      this.clickedCell = null
      this.displayUnitPopup = false
    },
    getUnitAttr<T>(
      preFilteredUnits: UnitData[],
      computeFnc: (payload: UnitData) => T | T[],
      cleanFnc: (x: T[]) => string | number | null
    ): ColumnCellData {
      const result: T[] = []
      const contributingUnits: typeof preFilteredUnits = []

      for (const unit of preFilteredUnits) {
        const computed = computeFnc(unit)
        if (Array.isArray(computed)) {
          let hasContribution = false
          for (const item of computed) {
            if (item != null && item !== false) {
              result.push(item)
              hasContribution = true
            }
          }
          if (hasContribution) {
            contributingUnits.push(unit)
          }
        } else if (computed != null) {
          result.push(computed)
          contributingUnits.push(unit)
        }
      }

      return {
        value: cleanFnc(result),
        units: contributingUnits
      }
    },
    exportCSV() {
      const exportDecimal = (num: number | string | null) => {
        return typeof num === 'number' ? Math.round(num * 100) / 100 : null
      }

      // Get the filtered and sorted columns that are currently visible
      const visibleColumns = this.columns.filter((col) => col.name !== 'label')

      const formatters: Record<[Column], (row: any) => string | number | null> = {
        [Column.NUMBER_LEASED]: (row) => row.number_leased?.value || '',
        [Column.CURRENTLY_AVAILABLE]: (row) => row.currently_available?.value || '',
        [Column.DAYS_ON_MARKET]: (row) =>
          row.days_on_market?.value ? Math.round(row.days_on_market.value) : '',
        [Column.MIN_SQFT]: (row) => (row.min_sqft?.value ? Math.round(row.min_sqft.value) : ''),
        [Column.SQFT]: (row) => (row.sqft?.value ? Math.round(row.sqft.value) : ''),
        [Column.MAX_SQFT]: (row) => (row.max_sqft?.value ? Math.round(row.max_sqft.value) : ''),
        [Column.MIN_MARKET_RENT]: (row) =>
          row.min_market_rent?.value ? Math.round(row.min_market_rent.value) : '',
        [Column.MARKET_RENT]: (row) =>
          row.market_rent?.value ? Math.round(row.market_rent.value) : '',
        [Column.MAX_MARKET_RENT]: (row) =>
          row.max_market_rent?.value ? Math.round(row.max_market_rent.value) : '',
        [Column.RENT_PSF]: (row) => exportDecimal(row.rent_psf?.value),
        [Column.EFFECTIVE_RENT]: (row) =>
          row.effective_rent?.value ? Math.round(row.effective_rent.value) : '',
        [Column.EFFECTIVE_RENT_PSF]: (row) => exportDecimal(row.effective_rent_psf?.value),
        [Column.CONCESSION_AMOUNT]: (row) =>
          row.concession_amount?.value ? Math.round(row.concession_amount.value) : '',
        [Column.TOTAL_CONCESSION_AMOUNT]: (row) =>
          row.total_concession_amount?.value ? Math.round(row.total_concession_amount.value) : '',
        [Column.CONCESSION_PERCENT]: (row) =>
          row.concession_percent?.value !== undefined && row.concession_percent?.value !== null
            ? exportDecimal((row.concession_percent.value as number) * 100)
            : '',
        [Column.YEAR_BUILT]: (row) => row.year_built?.value || '',
        [Column.TMLP]: (row) => (row.tmlp?.value ? Math.round(row.tmlp.value) : ''),
        [Column.NET_TMLP]: (row) => (row.net_tmlp?.value ? Math.round(row.net_tmlp.value) : ''),
        [Column.TMLP_FEES]: (row) => (row.tmlp_fees?.value ? Math.round(row.tmlp_fees.value) : ''),
        [Column.LABEL]: () => '',
        [Column.TREND]: () => ''
      }

      const data = this.rows.flatMap((row) => {
        const unitTypes = this.getChildrenRows(row).map((b) => ({
          ...b,
          label: `${b.label.split('/')[0]}BR / ${b.label.split('/')[1]}BA`,
          floorplans: this.getFloorplanRows(b)
        }))

        const result = [row]
        unitTypes.forEach((b) => {
          result.push(b)
          b.floorplans.forEach((floorplan) => {
            result.push({ ...floorplan, parentLabel: b.label })
          })
        })

        return result
      })

      const csvData = data.map((row) => {
        const isUnitType = row.label.includes('BR / ')
        const isFloorplan = row.parentLabel !== undefined
        const isBuilding = !isUnitType && !isFloorplan

        const csvRow: Record<string, string | number | null> = {
          Building: isBuilding ? row.label : '',
          'Unit Type': isUnitType ? row.label : '',
          'Floorplan Name': isFloorplan ? row.label : ''
        }

        // Add values for each visible column
        visibleColumns.forEach((col) => {
          const formatter = formatters[col.field]
          if (formatter) {
            csvRow[col.label] = formatter(row)
          }
        })

        return csvRow
      })

      const today = new Date().toISOString().split('T')[0]

      // Define column order
      const orderedColumns = ['Building', 'Unit Type', 'Floorplan Name']
      visibleColumns.forEach((col) => {
        if (!orderedColumns.includes(col.label) && col.label !== 'Trend') {
          orderedColumns.push(col.label)
        }
      })

      // sometimes there are commas, quotes, etc
      const escapeCSV = (value: any) => {
        if (
          typeof value === 'string' &&
          (value.includes(',') || value.includes('"') || value.includes('\n'))
        ) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }

      const headers = orderedColumns.map((col) => escapeCSV(col)).join(',')
      const csvContent =
        headers +
        '\n' +
        csvData
          .map((row) => orderedColumns.map((col) => escapeCSV(row[col] || '')).join(','))
          .join('\n')

      exportFile('hello-data-unit-type-table-' + today + '.csv', csvContent)
      this.$tracker.exported('unit_type_details')
    },
    getParentRow(
      prop: PropertyDetailsResponse,
      label: string,
      units: UnitData[]
    ): {
      effective_rent_psf: ColumnCellData
      rent_psf: ColumnCellData
      currently_available: ColumnCellData
      trend: { value: number | null; date: string }[]
      label: string
      total_concession_amount: ColumnCellData
      concession_amount: ColumnCellData
      concession_percent: ColumnCellData
      min_sqft: ColumnCellData
      max_market_rent: ColumnCellData
      min_market_rent: ColumnCellData
      days_on_market: ColumnCellData
      effective_rent: ColumnCellData
      max_sqft: ColumnCellData
      number_leased: ColumnCellData
      sqft: ColumnCellData
      year_built: ColumnCellData
      market_rent: ColumnCellData
      tmlp: ColumnCellData
      net_tmlp: ColumnCellData
      tmlp_fees: ColumnCellData
    } {
      const minDateStr = format(this.dateRange.from, 'yyyy-MM-dd')
      const maxDateStr = format(this.dateRange.to, 'yyyy-MM-dd')
      const newMaxDate = this.getMaxDate(prop)
      const newMaxDateStr = format(newMaxDate, 'yyyy-MM-dd')

      return {
        label,
        number_leased:
          this.groupBySelection === 'Unit Type'
            ? this.getUnitAttr(
                units,
                ({ period, prop: unitProp }) => {
                  const maxDateStr = format(this.getMaxDate(unitProp), 'yyyy-MM-dd')
                  return [period.exit_market && period.exit_market <= maxDateStr]
                },
                (x) => x.length
              )
            : this.getUnitAttr(
                units,
                ({ period }) => [period.exit_market && period.exit_market <= newMaxDateStr],
                (x) => x.length
              ),
        currently_available:
          this.groupBySelection === 'Unit Type'
            ? this.getUnitAttr(
                units,
                ({ lastHistory, period, prop: unitProp }) => {
                  const maxDateStr = format(this.getMaxDate(unitProp), 'yyyy-MM-dd')
                  return [lastHistory.to_date >= maxDateStr && period.exit_market !== maxDateStr]
                },
                (x) => x.length
              )
            : this.getUnitAttr(
                units,
                ({ lastHistory, period }) => {
                  return [
                    lastHistory.to_date >= newMaxDateStr && period.exit_market !== newMaxDateStr
                  ]
                },
                (x) => x.length
              ),
        total_concession_amount: this.getUnitAttr(
          units,
          ({ unit, lastHistory }) => {
            const leaseTermInMonths = unit.lease_term ?? 12
            if (lastHistory.price && lastHistory.effective_price) {
              return (lastHistory.price - lastHistory.effective_price) * leaseTermInMonths
            } else if (
              lastHistory.min_price &&
              lastHistory.min_effective_price &&
              lastHistory.max_price &&
              lastHistory.max_effective_price
            ) {
              return (
                ((lastHistory.min_price -
                  lastHistory.min_effective_price +
                  lastHistory.max_price -
                  lastHistory.max_effective_price) /
                  2.0) *
                leaseTermInMonths
              )
            }
            return null
          },
          avg
        ),
        concession_amount: this.getUnitAttr(
          units,
          ({ lastHistory }) => {
            if (lastHistory.price && lastHistory.effective_price) {
              return lastHistory.price - lastHistory.effective_price
            } else if (
              lastHistory.min_price &&
              lastHistory.min_effective_price &&
              lastHistory.max_price &&
              lastHistory.max_effective_price
            ) {
              return (
                (lastHistory.min_price -
                  lastHistory.min_effective_price +
                  lastHistory.max_price -
                  lastHistory.max_effective_price) /
                2.0
              )
            }
            return null
          },
          avg
        ),
        concession_percent: this.getUnitAttr(
          units,
          ({ lastHistory }) => {
            if (lastHistory.price && lastHistory.effective_price) {
              return (lastHistory.price - lastHistory.effective_price) / lastHistory.price
            } else if (
              lastHistory.min_price &&
              lastHistory.min_effective_price &&
              lastHistory.max_price &&
              lastHistory.max_effective_price
            ) {
              return (
                ((lastHistory.min_price - lastHistory.min_effective_price) / lastHistory.min_price +
                  (lastHistory.max_price - lastHistory.max_effective_price) /
                    lastHistory.max_price) /
                2.0
              )
            }
            return null
          },
          avg
        ),
        days_on_market: this.getUnitAttr(
          units,
          ({ period }) => {
            return daysOnMarket(period, this.dateRange.to)
          },
          avg
        ),
        min_market_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.price ?? lastHistory.min_price,
          safeMin
        ),
        market_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.price ?? lastHistory.min_price,
          avg
        ),
        tmlp: this.getUnitAttr(
          units,
          (unit) => {
            const baseRent = unit.lastHistory.price ?? unit.lastHistory.min_price
            if (!baseRent) {
              return null
            }
            return baseRent + getTMLPFees(unit.prop)
          },
          avg
        ),
        net_tmlp: this.getUnitAttr(
          units,
          (unit) => {
            const baseRent =
              unit.lastHistory.effective_price ?? unit.lastHistory.min_effective_price
            if (!baseRent) {
              return null
            }
            return baseRent + getTMLPFees(unit.prop)
          },
          avg
        ),
        tmlp_fees: this.getUnitAttr(
          units,
          (unit) => {
            return getTMLPFees(unit.prop)
          },
          avg
        ),
        max_market_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.price ?? lastHistory.max_price ?? lastHistory.min_price,
          safeMax
        ),
        effective_rent: this.getUnitAttr(
          units,
          ({ lastHistory }) => lastHistory.effective_price ?? lastHistory.min_effective_price,
          avg
        ),
        min_sqft: this.getUnitAttr(
          units,
          ({ unit }) => [unit.min_sqft, unit.sqft, unit.max_sqft],
          safeMin
        ),
        sqft: this.getUnitAttr(units, ({ unit }) => [unit.min_sqft, unit.sqft, unit.max_sqft], avg),
        max_sqft: this.getUnitAttr(
          units,
          ({ unit }) => [unit.min_sqft, unit.sqft, unit.max_sqft],
          safeMax
        ),
        rent_psf: this.getUnitAttr(
          units,
          ({ unit, lastHistory }) => [
            {
              sqft: unit.sqft || unit.min_sqft,
              price: lastHistory.price ?? lastHistory.min_price
            }
          ],
          avgPsf
        ),
        effective_rent_psf: this.getUnitAttr(
          units,
          ({ unit, lastHistory }) => [
            {
              sqft: unit.sqft || unit.min_sqft,
              price: lastHistory.effective_price ?? lastHistory.min_effective_price
            }
          ],
          avgPsf
        ),
        year_built:
          this.groupBySelection === 'Unit Type'
            ? this.getUnitAttr(
                units,
                (unit) => unit.prop.year_built,
                (values) => {
                  const avgValue = avg(values)
                  return avgValue !== null ? Math.round(avgValue) : null
                }
              )
            : {
                value: prop.year_built,
                units: []
              },
        trend: getTrend(units, this.expandedUnitHistory, minDateStr, maxDateStr, this.metric.value)
      }
    },
    toggleBed(bedRow: { label: string; id: string }, parentRow: { label: string; id: string }) {
      const bedId = bedRow.id
      const parentId = parentRow.id
      if (this.expandedBedRows.get(bedId) === parentId) {
        this.expandedBedRows.delete(bedId)
      } else {
        this.expandedBedRows.set(bedId, parentId)
        this.$tracker.expandedRow(`${parentRow.label}-${bedRow.label}`, 'unit_types')
      }
    },
    sortingChanged(colName: string) {
      if (this.currentSort?.column === colName) {
        if (this.currentSort?.direction === 'asc') {
          this.currentSort.direction = 'desc'
        } else {
          // if it's descending and clicked again, that means we no longer are sorting that column
          this.currentSort = null
        }
      } else {
        // set to ascending by default
        this.currentSort = { column: colName, direction: 'asc' }
      }
      this.$tracker.changedSort('unit_types', colName)
    },
    sortColumn(a: { value: number | null }, b: { value: number | null }) {
      return (a.value || 0) - (b.value || 0)
    },
    togglePinSubjectRow() {
      this.pinSubjectRow = !this.pinSubjectRow
      this.$tracker.changedFilter('pin_subject', 'unit_types', this.pinSubjectRow)
    },
    // Moves the subject row to the top, keeping the relative order of every other row
    pinSubjectFirst<T>(rows: T[], isSubject: (row: T) => boolean) {
      if (!this.pinSubjectRow || !this.subjectDetail) {
        return rows
      }
      const index = rows.findIndex(isSubject)
      if (index <= 0) {
        return rows
      }
      rows.unshift(...rows.splice(index, 1))
      return rows
    },
    // Replaces QTable's built-in sort so the subject can be pinned afterwards
    sortRows(
      rows: readonly { id: string; [key: string]: any }[],
      sortBy: string,
      descending: boolean
    ) {
      const column = this.columns.find((col) => col.name === sortBy)
      const sorted = [...rows]
      if (column?.sort) {
        const dir = descending ? -1 : 1
        sorted.sort((a, b) => column.sort(a[column.field], b[column.field], a, b) * dir)
      }
      return this.pinSubjectFirst(sorted, (row) => row.id === this.subjectDetail?.id)
    },
    togglePropertyRow(props: { row: { id: string }; expand: boolean }) {
      props.expand = !props.expand
      if (!props.expand) {
        // Clear only this parent's expanded bed rows when it collapses
        for (const [bedId, parentId] of this.expandedBedRows.entries()) {
          if (parentId === props.row.id) {
            this.expandedBedRows.delete(bedId)
          }
        }
      }
    },
    getColumnFormattedValue(columnField: Column, row: { [key: string]: number | string | null }) {
      const column = this.columns.find((_) => _.field === columnField)
      if (!column || !row[columnField]) {
        return ''
      }
      return column.format(row[columnField])
    },
    getMaxDate(prop: PropertyDetailsResponse) {
      return this.dateRange.to
        ? min([parseISO(prop.created_on), this.dateRange.to])
        : parseISO(prop.created_on)
    },
    openUnitPopup(
      title: string,
      fieldLabel: string,
      prop: PropertyDetailsResponse,
      field: {
        value: string | number | null
        formattedValue?: string
        unit?: string
      },
      units: UnitData[]
    ) {
      if (units.length) {
        this.$tracker.openedPopup(fieldLabel, 'unit_types')
        this.displayUnitPopup = true
        this.clickedCell = {
          title,
          fieldLabel,
          field,
          units
        }
      }
    },
    getChildrenRows(propRow: {
      prop: PropertyDetailsResponse
      units: {
        unit: Availability
        lastHistory: Price
        period: AvailabilityPeriod
        prop: PropertyDetailsResponse
      }[]
    }) {
      const rows = Object.values(
        propRow.units.reduce(
          (acc, obj) => {
            let keyValue
            if (this.groupBySelection === 'Property') {
              keyValue = `${obj.unit.bed}-${obj.unit.bath}-${obj.unit.partial_bath || '0'}`
            } else {
              keyValue = obj.prop.id
            }
            if (!acc[keyValue]) {
              acc[keyValue] = []
            }
            acc[keyValue].push(obj)
            return acc
          },
          {} as { [key: string]: UnitData[] }
        )
      )
        .filter((group: UnitData[]) => group.length > 0)
        .map((vals: UnitData[]) => ({
          id: this.getIdForChildRow(vals, propRow),
          prop: propRow.prop,
          units: vals,
          isSubject:
            this.groupBySelection === 'Unit Type' &&
            !!this.subjectDetail &&
            vals[0]?.prop.id === this.subjectDetail.id,
          ...this.getParentRow(propRow.prop, this.getLabelForChildRow(vals), vals)
        }))

      // Apply current sort if there is one
      if (this.currentSort) {
        const column = this.columns.find((col) => col.name === this.currentSort?.column)
        if (column && column.sort && column.sortable) {
          rows.sort((a, b) => {
            const valueA = a[column.field]
            const valueB = b[column.field]

            const result = column.sort(valueA, valueB, a, b)

            return this.currentSort?.direction === 'asc' ? -result : result
          })
          return this.pinSubjectFirst(rows, (row) => row.isSubject)
        }
      }

      // Default sort by bed/bath if no column sort is active
      rows.sort((a, b) => {
        const aParts = a.label.split('/')
        const bParts = b.label.split('/')
        const aBed = parseInt(aParts[0]) || 0
        const bBed = parseInt(bParts[0]) || 0
        const aBath = parseFloat(aParts[1]) || 0
        const bBath = parseFloat(bParts[1]) || 0

        return aBed - bBed || aBath - bBath
      })
      return this.pinSubjectFirst(rows, (row) => row.isSubject)
    },
    getFloorplanRows(unitTypeRow: {
      prop: PropertyDetailsResponse
      units: {
        unit: Availability
        lastHistory: Price
        period: AvailabilityPeriod
        prop: PropertyDetailsResponse
      }[]
    }) {
      // Group units by floorplan name
      const floorplanGroups = unitTypeRow.units.reduce(
        (acc, unit) => {
          const name =
            unit.unit.floorplan_name ||
            `${unit.unit.bed}BR / ${unit.unit.bath}${unit.unit.partial_bath ? '.5' : ''}BA`
          if (!acc[name]) {
            acc[name] = []
          }
          acc[name].push(unit)
          return acc
        },
        {} as { [key: string]: UnitData[] }
      )

      const rows = Object.entries(floorplanGroups).map(([name, units]) => ({
        name,
        prop: unitTypeRow.prop,
        units,
        ...this.getParentRow(unitTypeRow.prop, name, units)
      }))

      // Apply current sort if there is one
      if (this.currentSort) {
        const column = this.columns.find((col) => col.name === this.currentSort?.column)
        if (column && column.sort && column.sortable) {
          rows.sort((a, b) => {
            const valueA = a[column.field]
            const valueB = b[column.field]

            const result = column.sort(valueA, valueB, a, b)

            return this.currentSort?.direction === 'asc' ? -result : result
          })
          return rows
        }
      }

      return rows
    },
    getIdForChildRow(vals: UnitData[], propRow: { prop: { id: string } }) {
      const first = vals[0]
      if (!first) {
        return ''
      }

      switch (this.groupBySelection) {
        case 'Property':
          return `${propRow.prop.id}-${this.getLabelForChildRow(vals)}`
        case 'Unit Type':
          return `${first.unit.bed}/${first.unit.bath}${first.unit.partial_bath ? '.5' : ''}-${first.prop.id}`
        default:
          return ''
      }
    },
    getLabelForChildRow(vals: UnitData[]) {
      if (!vals.length) {
        return ''
      }

      switch (this.groupBySelection) {
        case 'Property':
          return `${vals[0].unit.bed}/${vals[0].unit.bath}${vals[0].unit.partial_bath ? '.5' : ''}`
        case 'Unit Type':
          return this.getPropertyDisplayName(vals[0].prop)
        default:
          return ''
      }
    },
    expandAllRows(props) {
      props.expand = true

      const unitTypeRows = this.getChildrenRows(props.row)
      for (const row of unitTypeRows) {
        this.expandedBedRows.set(row.id, props.row.id)
      }
      this.$tracker.expandedRow(`${props.row.label}-all`, 'unit_types')
    },
    getPropertyDisplayName(prop: PropertyDetailsResponse) {
      return prop.building_name || prop.street_address
    },
    getBedCount(row: { id: string; label: string }) {
      let splitBy
      if (this.groupBySelection === 'Unit Type') {
        splitBy = row.id
      } else {
        splitBy = row.label
      }
      return splitBy.split('/')[0]
    }
  }
}
</script>

<style scoped lang="scss">
.title-row td {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.constrained-cell {
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.floorplan-line {
  position: absolute;
  left: 36px;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 16px;
}

.label-cell {
  width: fit-content;
  white-space: nowrap;
  flex-wrap: nowrap;
}

.sticky-header-table {
  // height or max-height is important
  max-height: 60vh;

  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th {
    // bg color is important for th; just specify one
    background-color: white;
  }

  thead tr th {
    position: sticky;
    z-index: 1;
    background-color: white;
  }

  thead tr:first-child th {
    top: 0;
  }

  // this is when the loading indicator appears
  &.q-table--loading {
    thead tr:last-child th {
      // height of all previous header rows
      top: 48px;
    }
  }

  // prevent scrolling behind sticky top row on focus
  tbody {
    // height of all previous header rows
    scroll-margin-top: 48px;
  }

  // Make the footer row sticky at the bottom
  .title-row {
    position: sticky;
    bottom: 0;
    z-index: 1;
    background-color: white;
  }
}

// Dark mode specific overrides
body.body--dark .sticky-header-table {
  .q-table__top,
  .q-table__bottom,
  thead tr:first-child th,
  thead tr th,
  .title-row {
    background-color: var(--q-dark-page);
  }
}

.visible-on-hover {
  opacity: 0;
}
.visible-on-hover-trigger:hover .visible-on-hover {
  opacity: 1;
}
</style>
